import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePersistStore, useStore } from "../../store";
import { useBuyerReviews } from "../../hooks/queries";
import Button from "../../controls/Button";
import { ReviewStatusChip } from "../../components/status";
import SortDropdown from "../../components/SortDropdown";

// Per-row seller-review status. `null` = buyer hasn't submitted yet; the
// REVIEW_STATUS values match api/utils/enums.js (kept as strings here to
// avoid a cross-package enum import).
type SellerStatus = null | "pending" | "approved" | "rejected";

type FilterValue =
  | "all"
  | "not-started"
  | "under-moderation"
  | "approved"
  | "rejected";

const FILTERS: { label: string; value: FilterValue }[] = [
  { label: "All", value: "all" },
  { label: "Not started", value: "not-started" },
  { label: "Under moderation", value: "under-moderation" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const matchesFilter = (sellerStatus: SellerStatus, filter: FilterValue) => {
  switch (filter) {
    case "all":
      return true;
    case "not-started":
      return sellerStatus === null;
    case "under-moderation":
      return sellerStatus === "pending";
    case "approved":
      return sellerStatus === "approved";
    case "rejected":
      return sellerStatus === "rejected";
    default:
      return true;
  }
};

function Reviews() {
  const navigate = useNavigate();
  const { token } = usePersistStore((s: any) => s);
  const { setErrorMessage } = useStore((s: any) => s);
  const [filter, setFilter] = useState<FilterValue>("all");

  const { data, isLoading, error } = useBuyerReviews(token);
  const list: any[] = useMemo(() => data || [], [data]);
  const isReady = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);

  const filtered = useMemo(
    () =>
      list.filter((item) =>
        matchesFilter(item.sellerReviewStatus as SellerStatus, filter)
      ),
    [list, filter]
  );

  const total = list.length;
  const shown = filtered.length;

  return (
    <div className="bg-pink p-4">
      <div className="row">
        <div className="col-12">
          <div className="chartCard py-4">
            <div className="tableCardHeader px-4">
              <div className="tableCardHeader__titleBlock">
                <h2 className="tableCardHeader__title m-0">Reviews</h2>
                <p className="tableCardHeader__subtitle m-0">
                  {!isReady
                    ? "Loading your purchases…"
                    : total === 0
                    ? "You don't have any reviewable purchases yet"
                    : filter === "all"
                    ? `${total} purchase${total === 1 ? "" : "s"}`
                    : `${shown} of ${total}`}
                </p>
              </div>
              <div className="tableCardHeader__actions">
                <SortDropdown
                  label="Status"
                  icon="bi-funnel"
                  options={FILTERS}
                  value={filter}
                  onChange={(v) => setFilter(v as FilterValue)}
                />
              </div>
            </div>

            <div className="pendingReviewList">
              {!isReady &&
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    className="pendingReviewItem pendingReviewItem--skeleton"
                    key={i}
                  >
                    <div className="pendingReviewItem__img pendingReviewItem__img--skeleton" />
                    <div className="pendingReviewItem__body">
                      <div className="pendingReviewItem__skelLine pendingReviewItem__skelLine--title" />
                      <div className="pendingReviewItem__skelLine pendingReviewItem__skelLine--sub" />
                      <div className="pendingReviewItem__skelLine pendingReviewItem__skelLine--pills" />
                    </div>
                    <div className="pendingReviewItem__skelBtn" />
                  </div>
                ))}

              {isReady && total === 0 && (
                <div className="pendingReviewEmpty">
                  <i className="bi bi-chat-heart pendingReviewEmpty__icon"></i>
                  <h3 className="pendingReviewEmpty__title">No reviews yet</h3>
                  <p className="pendingReviewEmpty__text">
                    When your purchases are delivered, you'll be able to review
                    them here.
                  </p>
                </div>
              )}

              {isReady && total > 0 && shown === 0 && (
                <div className="pendingReviewEmpty">
                  <i className="bi bi-funnel pendingReviewEmpty__icon"></i>
                  <h3 className="pendingReviewEmpty__title">No matches</h3>
                  <p className="pendingReviewEmpty__text">
                    No reviews match the selected filter. Try another one.
                  </p>
                </div>
              )}

              {isReady &&
                filtered.map((item) => {
                  const needsAnyReview =
                    !item.hasSellerReview || !item.hasPlatformReview;
                  return (
                    <div key={item._id} className="pendingReviewItem">
                      <Link
                        to={`/details/${item._id}`}
                        className="pendingReviewItem__imgLink"
                        aria-label={`Open ${item.title}`}
                      >
                        <img
                          src={item?.images?.[0] || "/assets/imgs/crawl.png"}
                          alt={item.title}
                          className="pendingReviewItem__img"
                        />
                      </Link>
                      <div className="pendingReviewItem__body">
                        <Link
                          to={`/details/${item._id}`}
                          className="pendingReviewItem__titleLink"
                        >
                          <h4
                            className="pendingReviewItem__title"
                            title={item.title}
                          >
                            {item.title}
                          </h4>
                        </Link>
                        <p className="pendingReviewItem__seller">
                          Sold by{" "}
                          <Link
                            to={`/seller/${item?.seller?._id}`}
                            className="pendingReviewItem__sellerName"
                          >
                            {item?.seller?.name}
                          </Link>
                        </p>
                        <div className="pendingReviewItem__statuses">
                          <div className="pendingReviewItem__statusRow">
                            <span className="pendingReviewItem__statusLabel">
                              Seller
                            </span>
                            <ReviewStatusChip
                              hasReview={!!item.hasSellerReview}
                              status={
                                (item.sellerReviewStatus as SellerStatus) ||
                                undefined
                              }
                            />
                          </div>
                          <div className="pendingReviewItem__statusRow">
                            <span className="pendingReviewItem__statusLabel">
                              Platform
                            </span>
                            <ReviewStatusChip
                              hasReview={!!item.hasPlatformReview}
                              platformSubmitted={!!item.hasPlatformReview}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="pendingReviewItem__action">
                        <Button
                          title={needsAnyReview ? "Review now" : "View"}
                          onClick={() =>
                            navigate(`/dashboard/review/${item._id}`)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reviews;
